import { LoadingButton } from "@mui/lab";
import { Box, Grid } from "@mui/material";
import FormikControl from "../../../bmr/components/common/FormikControl";

import { useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import { useFormik } from "formik";
import {
  assignedPermission,
  fetchAllAssignedTemplates,
  setSelectedRolesFormatted,
  updateAssignTemplatePermission,
} from "../slice/FbAssignedPermission";
import { disableAddModal } from "../../../bmr/slices/onBoardUser";
import { useNavigate } from "react-router";

const validationSchema = Yup.object().shape({
  template_id: Yup.string().required("Template is required"),
  product_fb_type_id: Yup.string().required("Type is required"),
  version: Yup.string().required("Version number is required"),
  approval_level: Yup.number().required("Approval level is required"),
  product_id: Yup.number().required("Product is required"),
  phase_id: Yup.number().required("Phase is required"),
});

const AssignNewPermission = ({ editId, fetchData, productData, phaseData }) => {
  const dispatch = useDispatch();
  const { templateDropdownlist } = useSelector((state) => state?.bmrFbl);
  const { combinedRoles } = useSelector((state) => state?.bmrRoles);
  const Generic = useSelector((state) => state.assignedTemplates);
  const isLoading = useSelector((state) => state?.bmrOnBoardUser?.loading);
  const navigate = useNavigate();
  // Helper function to get role ID by role name
  const getRoleIdByName = (roleName) => {
    const role = combinedRoles?.find(
      (r) => r.title?.toLowerCase() === roleName?.trim().toLowerCase(),
    );
    return role?.id || "";
  };

  // Helper function to parse approval level (e.g., "2-Level" -> 2)
  const parseApprovalLevel = (level) => {
    if (!level) return "";
    const match = level.toString().match(/\d+/);
    return match ? parseInt(match[0], 10) : "";
  };

  // Build initial values based on fetchData (edit mode) or empty (add mode)
  const getInitialValues = () => {
    const baseValues = {
      template_id: "",
      product_fb_type_id: "",
      version: "",
      approval_level: "",
      product_id: "",
      phase_id: "",
    };

    if (!fetchData || Object.keys(fetchData).length === 0) {
      return baseValues;
    }

    // Parse approval level from "2-Level" to number 2
    const parsedApprovalLevel = parseApprovalLevel(fetchData.approval_level);

    // Parse roles_for_approval (e.g., "Warehouse, Manufacture")
    const rolesArray = fetchData.roles_for_approval
      ? fetchData.roles_for_approval.split(",")
      : [];

    // Create approval_X fields for each level
    const approvalFields = {};
    for (let i = 1; i <= parsedApprovalLevel; i++) {
      approvalFields[`approval_${i}`] = rolesArray[i - 1]
        ? getRoleIdByName(rolesArray[i - 1])
        : "";
    }

    return {
      ...baseValues,
      template_id: fetchData.template_id || "",
      product_fb_type_id: fetchData.product_fb_type_id || "",
      version: fetchData.version || "",
      approval_level: parsedApprovalLevel || "",
      product_id: fetchData.product_id || "",
      phase_id: fetchData.phase_id || "",
      ...approvalFields,
    };
  };

  const initialValues = getInitialValues();

  const handleSubmit = async (values) => {
    const levelCount = Number(values.approval_level);

    const approvals = Array.from({ length: levelCount }, (_, i) => {
      const level = i + 1;
      return {
        role_id: values[`approval_${level}`],
        level,
      };
    }).filter((item) => item.role_id);

    const payload = {
      template_id: values.template_id,
      product_fb_type_id: values.product_fb_type_id,
      version: values.version,
      approval_level: levelCount,
      product_id: values.product_id,
      phase_id: values.phase_id,
      approvals,
    };

    let templateId;
    let assignedId;

    if (editId) {
      // ── EDIT MODE ──────────────────────────────────────────────────────
      const response = await dispatch(
        updateAssignTemplatePermission({
          id: editId, //  assignedId passed as id
          ...payload,
        }),
      ).unwrap();
      //  Build approvedRole from response
      const approvedRole = response?.responseObj?.approvals?.map((item) => ({
        value: item?.role_id,
        label: item?.role_name,
      }));

      //  Update selectedRolesFormatted in Redux
      await dispatch(setSelectedRolesFormatted(approvedRole));

      templateId = response?.responseObj?.template_id;
      assignedId = response?.responseObj?.id || editId;
    } else {
      // ── ADD MODE ───────────────────────────────────────────────────────
      const response = await dispatch(assignedPermission(payload)).unwrap();
      //  Build approvedRole from response
      const approvedRole = response?.responseObj?.approvals?.map((item) => ({
        value: item?.role_id,
        label: item?.role_name,
      }));
      //  Update selectedRolesFormatted in Redux
      await dispatch(setSelectedRolesFormatted(approvedRole));

      templateId = response?.responseObj?.template_id;
      assignedId = response?.responseObj?.id;
    }

    await dispatch(fetchAllAssignedTemplates());
    dispatch(disableAddModal());

    // Navigate to section permission page for both add and edit
    navigate(
      `/bmr/assigned-permission/assign-section-permission/${templateId}/${assignedId}`,
    );
  };

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: handleSubmit,
  });
const templateOptions = useMemo(
  () =>
    templateDropdownlist
      ?.filter((item) =>
        item?.versions?.some((version) => version.status === 2),
      )
      ?.map((item) => ({
        label: item.name,
        value: item.id,
      })) || [],
  [templateDropdownlist],
);

const versionOptions = useMemo(() => {
  const selected = templateDropdownlist?.find(
    (t) => t.id === formik.values.template_id,
  );

  return (
    selected?.versions
      ?.filter((item) => item.status === 2)
      ?.map((v) => ({
        label: `V. ${v.version}`,
        value: v.version,
      })) || []
  );
}, [formik.values.template_id, templateDropdownlist]);

  const typeOptions = useMemo(
    () =>
      Generic.productDropdown?.map((item) => ({
        label: item.type,
        value: item.id,
      })) || [],
    [Generic.productDropdown],
  );

  const rolesOptions = useMemo(
    () =>
      combinedRoles?.map((item) => ({
        label: item.title,
        value: item.id,
      })) || [],
    [combinedRoles],
  );

  const filteredPhaseOptions = useMemo(() => {
    if (!formik.values.product_id) return [];
    return phaseData.filter(
      (phase) => phase.productId === formik.values.product_id,
    );
  }, [formik.values.product_id, phaseData]);

  const prevProductRef = useRef();

  useEffect(() => {
    if (
      prevProductRef.current &&
      prevProductRef.current !== formik.values.product_id
    ) {
      formik.setFieldValue("phase_id", "");
    }
    prevProductRef.current = formik.values.product_id;
  }, [formik.values.product_id]);

  // ================= APPROVAL GENERATOR =================

  const generateApprovalLevels = (levelCount) => {
    if (!levelCount) return [];

    const fields = Array.from({ length: levelCount }, (_, i) => {
      const level = i + 1;
      return {
        label: `Level ${level} Approval`,
        name: `approval_${level}`,
        type: "select",
        options: rolesOptions,
        required: true,
      };
    });

    return [
      {
        section: `Roles for ${levelCount}-Level Approval`,
        inLine: fields,
      },
    ];
  };

  // ================= FORM OPTIONS =================

  const formOptions = [
    {
      label: "Template",
      type: "select",
      name: "template_id",
      required: true,
      placeholder: "Select template",
      options: templateOptions,
    },
    {
      label: "Versions",
      type: "select",
      name: "version",
      required: true,
      placeholder: "Select version",
      options: versionOptions,
    },
    {
      label: "Product",
      type: "select",
      name: "product_id",
      required: true,
      placeholder: "Select Product",
      options: productData,
    },
    {
      label: "Phase",
      type: "select",
      name: "phase_id",
      required: true,
      placeholder: "Select Phase",
      options: filteredPhaseOptions,
      disabled: !formik.values.product_id,
    },
    {
      label: "Type",
      type: "select",
      name: "product_fb_type_id",
      required: true,
      placeholder: "Select Type",
      options: typeOptions,
    },
    {
      label: "Approval Level",
      type: "select",
      name: "approval_level",
      required: true,
      placeholder: "Select approval level",
      options: Array.from({ length: 10 }, (_, i) => ({
        label: `${i + 1} level approval`,
        value: i + 1,
      })),
    },
    ...(formik.values.approval_level
      ? generateApprovalLevels(Number(formik.values.approval_level))
      : []),
  ];

  return (
    <Box>
      <form onSubmit={formik.handleSubmit}>
        <Grid container spacing={2} className="mb-3">
          {formOptions.map((props, key) => {
            // If it's a section header with inline fields
            if (props.section && props.inLine) {
              return (
                <Grid key={key} item xs={12}>
                  <strong className="!pb-3 block">{props.section}</strong>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                    {props.inLine.map((fieldProps, fieldKey) => (
                      <Box
                        key={fieldKey}
                        sx={{ minWidth: "45%", flex: "1 1 45%" }}
                      >
                        <FormikControl formik={formik} {...fieldProps} />
                      </Box>
                    ))}
                  </Box>
                </Grid>
              );
            }

            // If it's just a section header
            if (props.section) {
              return (
                <Grid key={key} item xs={12}>
                  <strong className="!pb-3 block">{props.section}</strong>
                </Grid>
              );
            }

            // Otherwise, it's a regular form field
            return (
              <Grid key={key} item xs={12}>
                <FormikControl formik={formik} {...props} />
              </Grid>
            );
          })}
        </Grid>
        <Grid container spacing={2} className="mt-3">
          <Grid item xs={12}>
            <LoadingButton
              loading={isLoading}
              fullWidth
              type="submit"
              variant="contained"
            >
              {editId ? "Update" : "Submit"}
            </LoadingButton>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};

export default AssignNewPermission;
